import React, { useEffect, useState, useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { DestinationAPI, FloorAPI, NavigationAPI, ShapeAPI, resolveAsset } from "../../../services/api";
import LeafletFloorMap from "../../../components/LeafletFloorMap";
import { parseNames } from "../../../utils/nameParser";
import { ReactComponent as BathroomIcon } from "../../../assets/icons/navigation/bathroom.svg";
import { ReactComponent as ClickIcon } from "../../../assets/icons/navigation/click.svg";
import { ReactComponent as ElevatorIcon } from "../../../assets/icons/navigation/elevator.svg";
import { ReactComponent as EyeIcon } from "../../../assets/icons/navigation/eye.svg";
import { ReactComponent as HandIcon } from "../../../assets/icons/navigation/hand.svg";
import { ReactComponent as LocationIcon } from "../../../assets/icons/addlocation/location.svg";
import { ReactComponent as PlusIcon } from "../../../assets/icons/navigation/plus.svg";
import { ReactComponent as StairsIcon } from "../../../assets/icons/navigation/stairs.svg";
import { ReactComponent as UploadIcon } from "../../../assets/icons/addlocation/upload.svg";
import { ReactComponent as CounterServiceIcon } from "../../../assets/icons/addlocation/room.svg";

function getFloorLabel(f, t) {
  const name = f.name || "";
  const level = f.level;
  if (!name) {
    return t("admin.navigation.floorLevel", { defaultValue: `ชั้น ${level}`, level });
  }
  const lowerName = name.toLowerCase().trim();
  const isGeneric =
    lowerName === `floor${level}` ||
    lowerName === `floor ${level}` ||
    lowerName === `floor_${level}` ||
    lowerName === `ชั้น${level}` ||
    lowerName === `ชั้น ${level}` ||
    lowerName === `ชั้นที่${level}` ||
    lowerName === `ชั้นที่ ${level}`;
  if (isGeneric) {
    return t("admin.navigation.floorLevel", { defaultValue: `ชั้น ${level}`, level });
  }
  return name;
}

// ===== ข้อมูล mock สำหรับแต่ละประเภทสถานที่เพิ่มเติม =====
const LOCATION_POINTS = {
  bathroom: [
    {
      id: "ID#BR01", x: 30, y: 80, text: "ห้องน้ำชาย",
      icon: BathroomIcon, color: "blue",
      img: "https://www.cmhy.city/uploads/photos/85-Services/115-Toilet-Service/2510-toilet--suandok-park/place2510_IMG_20201224_1551379.jpg",
    },
    {
      id: "ID#BR02", x: 36, y: 85, text: "ห้องน้ำหญิง",
      icon: BathroomIcon, color: "blue",
      img: "https://www.cmhy.city/uploads/photos/85-Services/115-Toilet-Service/2510-toilet--suandok-park/place2510_IMG_20201224_1551379.jpg",
    },
  ],
  stairs: [
    {
      id: "ID#ST01", x: 50, y: 28, text: "บันไดทางทิศเหนือที่ 1",
      icon: StairsIcon, color: "orange",
      img: "https://encrypted-tbn0.gstatic.com/images?q=tbn:ANd9GcTfR3a24oGapnqJKsctLmUEzhlfNi27YWTxDQ&s",
    },
    {
      id: "ID#ST02", x: 80, y: 20, text: "บันไดทางทิศเหนือที่ 2",
      icon: StairsIcon, color: "orange",
      img: "https://encrypted-tbn0.gstatic.com/images?q=tbn:ANd9GcTfR3a24oGapnqJKsctLmUEzhlfNi27YWTxDQ&s",
    },
    {
      id: "ID#ST03", x: 47, y: 90, text: "บันไดทางทิศใต้ที่ 1",
      icon: StairsIcon, color: "orange",
      img: "https://encrypted-tbn0.gstatic.com/images?q=tbn:ANd9GcTfR3a24oGapnqJKsctLmUEzhlfNi27YWTxDQ&s",
    },
  ],
  elevator: [
    {
      id: "ID#EV01", x: 80, y: 45, text: "ลิฟท์ 1",
      icon: ElevatorIcon, color: "green",
      img: "https://www.med.cmu.ac.th/web/files/2023/02/S__2744507-1024x593.jpg",
    },
    {
      id: "ID#EV02", x: 78, y: 62, text: "ลิฟท์ 2",
      icon: ElevatorIcon, color: "green",
      img: "https://www.med.cmu.ac.th/web/files/2023/02/S__2744507-1024x593.jpg",
    },
  ],
  counterservice: [
    {
      id: "ID#CS01", x: 50, y: 50, text: "จุดบริการสอบถาม 1",
      icon: CounterServiceIcon, color: "purple",
      img: "https://encrypted-tbn0.gstatic.com/images?q=tbn:ANd9GcSc8NZahjb34xyaekxbEF1OJ7FUgtpm2HLJvw&s",
    },
    {
      id: "ID#CS02", x: 66, y: 75, text: "จุดบริการสอบถาม 2",
      icon: CounterServiceIcon, color: "purple",
      img: "https://encrypted-tbn0.gstatic.com/images?q=tbn:ANd9GcSc8NZahjb34xyaekxbEF1OJ7FUgtpm2HLJvw&s",
    },
  ],
};

const ALL_POINTS = Object.values(LOCATION_POINTS).flat();



const CATEGORY_META = {
  bathroom: {
    icon: BathroomIcon,
    color: "blue",
    defaultText: "ห้องน้ำ",
  },
  stairs: {
    icon: StairsIcon,
    color: "orange",
    defaultText: "บันได",
  },
  elevator: {
    icon: ElevatorIcon,
    color: "green",
    defaultText: "ลิฟท์",
  },
  counterservice: {
    icon: CounterServiceIcon,
    color: "purple",
    defaultText: "จุดบริการ",
  },
};

const TOOLS = [
  { id: "mouse", icon: ClickIcon },
  { id: "hand", icon: HandIcon },
  { id: "eye", icon: EyeIcon },
  { id: "stairs", icon: StairsIcon },
  { id: "elevator", icon: ElevatorIcon },
  { id: "bathroom", icon: BathroomIcon },
  { id: "counterservice", icon: CounterServiceIcon },
];

const shapeBelongsToFloor = (shape, floorId) => {
  const shapeFloorId = shape.floorId ?? shape.floor_id ?? shape.floor?.id;
  return shapeFloorId == null || String(shapeFloorId) === String(floorId);
};

export default function AdditionalLocationEdit() {
  const { t } = useTranslation();
  const [searchParamsAL, setSearchParamsAL] = useSearchParams();

  const getCategoryKey = (cat) => {
    if (cat === "counterservice") return "counterService";
    return cat;
  };

  const getToolTitle = (id) => {
    if (id === "mouse") return t("admin.additionalLocation.toolSelectEdit", "เลือก/แก้ไข");
    if (id === "hand") return t("admin.additionalLocation.toolPanDrag", "เลื่อน/ลาก");
    if (id === "eye") return t("admin.additionalLocation.toolView", "ดูแผนที่");
    const key = id === "counterservice" ? "counterService" : id;
    return t(`admin.additionalLocation.${key}`);
  };
  const floorIdParam = searchParamsAL.get("floorId");
  const buildingId = searchParamsAL.get("buildingId");

  const [floorOptions, setFloorOptions] = useState([]);
  const [floorData, setFloorData] = useState(null);
  const [shapes, setShapes] = useState([]);
  const [showConfirmDelete, setShowConfirmDelete] = useState(false);

  const [activeTool, setActiveTool] = useState("mouse");
  const [activePoint, setActivePoint] = useState(null);

  const [serverDestinations, setServerDestinations] = useState(null);
  const [form, setForm] = useState({
    name: "",
    category: "bathroom",
    x: "",
    y: "",
  });
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [saving, setSaving] = useState(false);
  const [errMsg, setErrMsg] = useState("");
  const [names, setNames] = useState([""]);

  useEffect(() => {
    if (!imageFile) {
      setImagePreview(null);
      return;
    }
    const objectUrl = URL.createObjectURL(imageFile);
    setImagePreview(objectUrl);
    return () => URL.revokeObjectURL(objectUrl);
  }, [imageFile]);

  // Load floors of the building
  useEffect(() => {
    if (!buildingId) return;
    FloorAPI.listByBuilding(buildingId)
      .then((data) => {
        const list = Array.isArray(data) ? data : data?.items || [];
        setFloorOptions(list);
        if (!floorIdParam && list.length > 0) {
          setSearchParamsAL(
            { buildingId, floorId: String(list[0].id) },
            { replace: true }
          );
        }
      })
      .catch(() => { });
  }, [buildingId, floorIdParam, setSearchParamsAL]);

  // Load active floor details
  useEffect(() => {
    if (!floorIdParam) {
      setFloorData(null);
      return;
    }
    NavigationAPI.getFloor(floorIdParam)
      .then((d) => setFloorData(d))
      .catch(() => setFloorData(null));
  }, [floorIdParam]);

  // Load shapes context (rooms)
  useEffect(() => {
    if (!floorIdParam) {
      setShapes([]);
      return;
    }
    setShapes([]);
    ShapeAPI.listByFloor(floorIdParam)
      .then((d) =>
        setShapes(
          (Array.isArray(d) ? d : []).filter((shape) =>
            shapeBelongsToFloor(shape, floorIdParam)
          )
        )
      )
      .catch(() => setShapes([]));
  }, [floorIdParam]);

  const reload = () => {
    if (!floorIdParam) return;
    DestinationAPI.listByFloor(floorIdParam)
      .then((d) => setServerDestinations(Array.isArray(d) ? d : []))
      .catch(() => { });
  };

  useEffect(reload, [floorIdParam]);

  // Resolve points
  const apiPoints = useMemo(() => {
    return (serverDestinations || []).map((d) => {
      const meta = CATEGORY_META[d.category] || CATEGORY_META.bathroom;
      return {
        id: d.id,
        x: d.x,
        y: d.y,
        text: d.name || d.text || t(`admin.additionalLocation.${getCategoryKey(d.category)}`, meta.defaultText),
        category: d.category,
        icon: meta.icon,
        color: meta.color,
        img: d.img || d.imageUrl,
      };
    });
  }, [serverDestinations, t]);

  const points = serverDestinations ? apiPoints : ALL_POINTS;

  useEffect(() => {
    if (!activePoint) return;
    const p = points.find((x) => x.id === activePoint);
    if (p) {
      const rawName = p.name || p.text || "";
      const parsed = parseNames(rawName);
      setNames(parsed.all.length > 0 ? parsed.all : [""]);
      setForm({
        name: rawName,
        category: p.category || "bathroom",
        x: String(p.x ?? ""),
        y: String(p.y ?? ""),
      });
      setActiveTool("mouse");
      setImageFile(null);
    }
  }, [activePoint, points]);

  // Modify points array for live coordinates edit or draft preview
  const displayedPoints = points.map((p) => {
    if (activePoint && p.id === activePoint) {
      const meta = CATEGORY_META[form.category] || CATEGORY_META.bathroom;
      const combinedName = names.map(n => n.trim()).filter(Boolean).join(", ");
      return {
        ...p,
        x: form.x !== "" ? Number(form.x) : p.x,
        y: form.y !== "" ? Number(form.y) : p.y,
        text: combinedName || p.name || p.text,
        category: form.category,
        icon: meta.icon,
        color: meta.color,
        img: imagePreview || p.img || p.imageUrl,
      };
    }
    return p;
  });

  if (!activePoint && form.x !== "" && form.y !== "") {
    const meta = CATEGORY_META[form.category] || CATEGORY_META.bathroom;
    const combinedName = names.map(n => n.trim()).filter(Boolean).join(", ");
    displayedPoints.push({
      id: "draft",
      x: Number(form.x),
      y: Number(form.y),
      text: combinedName || t("admin.additionalLocation.newLocationDraft", "จุดบริการใหม่ (ดราฟต์)"),
      category: form.category,
      icon: meta.icon,
      color: meta.color,
      img: imagePreview || null,
      isDraft: true,
    });
  }

  const onMapClick = (px) => {
    const poiCategories = ["stairs", "elevator", "bathroom", "counterservice"];
    if (activeTool !== "mouse" && !poiCategories.includes(activeTool)) return;

    if (poiCategories.includes(activeTool)) {
      setActivePoint(null);
      setForm((f) => ({
        ...f,
        category: activeTool,
        x: String(Math.round(px.x)),
        y: String(Math.round(px.y)),
      }));
    } else if (activeTool === "mouse") {
      setForm((f) => ({
        ...f,
        x: String(Math.round(px.x)),
        y: String(Math.round(px.y)),
      }));
    }
  };

  const markers = useMemo(() => {
    return displayedPoints.map((p) => ({
      id: p.id,
      type: p.category === "stairs" ? "stair" : p.category === "elevator" ? "elevator" : p.category === "bathroom" ? "bathroom" : p.category === "counterservice" ? "counterservice" : "poi",
      x: Number(p.x) || 0,
      y: Number(p.y) || 0,
      imageUrl: p.img ? resolveAsset(p.img) : null,
      label: parseNames(p.text).primary || (p.id === "draft" ? t("admin.additionalLocation.newLocationDraft", "จุดบริการใหม่ (ดราฟต์)") : ""),
      onClick: () => {
        if (p.id !== "draft") {
          setActivePoint((prev) => (String(prev) === String(p.id) ? null : p.id));
        }
      },
    }));
  }, [displayedPoints, t]);

  const handleFloorClick = (fId) => {
    setSearchParamsAL({ buildingId, floorId: String(fId) });
    setActivePoint(null);
    setForm({ name: "", category: "bathroom", x: "", y: "" });
    setNames([""]);
    setImageFile(null);
  };

  const onSave = async () => {
    setErrMsg("");
    if (!floorIdParam) {
      setErrMsg(t("admin.additionalLocation.noFloorId", "ไม่พบ floorId ใน URL"));
      return;
    }
    setSaving(true);
    try {
      const combinedName = names.map(n => n.trim()).filter(Boolean).join(", ");
      const payload = {
        name: combinedName,
        category: form.category,
        x: Number(form.x) || 0,
        y: Number(form.y) || 0,
        image: imageFile || undefined,
      };
      if (activePoint) {
        await DestinationAPI.update(activePoint, payload);
      } else {
        await DestinationAPI.create(floorIdParam, payload);
      }
      setForm({ name: "", category: "bathroom", x: "", y: "" });
      setNames([""]);
      setImageFile(null);
      setActivePoint(null);
      reload();
    } catch (e) {
      setErrMsg(e.message || t("admin.additionalLocation.saveFailed", "บันทึกไม่สำเร็จ"));
    } finally {
      setSaving(false);
    }
  };

  const handleConfirmDelete = async () => {
    if (!activePoint) return;
    setShowConfirmDelete(false);
    try {
      await DestinationAPI.remove(activePoint);
      setActivePoint(null);
      setForm({ name: "", category: "bathroom", x: "", y: "" });
      setNames([""]);
      reload();
    } catch (e) {
      alert(e.message || t("admin.additionalLocation.deleteFailed", "ลบไม่สำเร็จ"));
    }
  };

  const getUploadPreviewUrl = () => {
    if (imagePreview) {
      return imagePreview;
    }
    if (activePoint) {
      const p = points.find((x) => x.id === activePoint);
      if (p) {
        return resolveAsset(p.img || p.imageUrl);
      }
    }
    return "https://w2.med.cmu.ac.th/opd/wp-content/uploads/2020/02/IMG_2237-1024x768.jpg?x48287";
  };

  const bgImage = resolveAsset(floorData?.imageUrl);

  return (
    <div className="font-sans w-full h-full overflow-hidden">
      <div className="flex w-full h-full overflow-hidden">

        {/* ===== LEFT: แผนที่ 70% ===== */}
        <div className="w-[70%] min-w-0 flex flex-col">

          {/* ---- Top bar: เมนูชั้น + tools ---- */}
          <div className="flex items-center justify-between bg-white px-3 shrink-0 border-b border-gray-200" style={{ height: '72px' }}>

            {/* เมนูเลือกชั้น */}
            <div className="flex items-center gap-1 overflow-x-auto mr-8">
              {floorOptions.map((f) => (
                <button
                  key={f.id}
                  onClick={() => handleFloorClick(f.id)}
                  className={`px-5 h-10 rounded-lg text-sm font-semibold whitespace-nowrap transition-colors ${String(f.id) === String(floorIdParam)
                    ? "bg-blue-600 text-white"
                    : "bg-white text-slate-500 hover:bg-gray-100"
                    }`}
                >
                  {getFloorLabel(f, t)}
                </button>
              ))}
              {floorOptions.length === 0 && (
                <div className="text-slate-400 text-xs px-4">{t("admin.additionalLocation.loadingFloors", "กำลังโหลดชั้น...")}</div>
              )}
            </div>

            {/* เครื่องมือ + zoom */}
            <div className="flex items-center gap-0.5">
              {TOOLS.map((tool) => {
                const Icon = tool.icon;
                return (
                  <button
                    key={tool.id}
                    title={getToolTitle(tool.id)}
                    onClick={() => {
                      setActiveTool(tool.id);
                      const poiCategories = ["stairs", "elevator", "bathroom", "counterservice"];
                      if (poiCategories.includes(tool.id)) {
                        setForm((f) => ({ ...f, category: tool.id }));
                      }
                    }}
                    aria-pressed={activeTool === tool.id}
                    className={`w-10 h-10 flex items-center justify-center rounded-lg transition-colors ${activeTool === tool.id
                      ? "bg-blue-600 text-white"
                      : "text-slate-600 hover:bg-gray-100"
                      }`}
                  >
                    <Icon className="w-4 h-4" />
                  </button>
                );
              })}

              <button className="w-10 h-10 flex items-center justify-center rounded-lg text-slate-600 hover:bg-gray-100">
                <PlusIcon className="w-3 h-3" />
              </button>

              <div className="px-3 text-slate-500 text-sm font-medium">100%</div>

              <button className="w-10 h-10 flex items-center justify-center rounded-lg text-slate-600 hover:bg-gray-100">
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" className="w-3 h-3"><line x1="5" y1="12" x2="19" y2="12" /></svg>
              </button>
            </div>
          </div>

          {/* ---- แผนที่พร้อม LeafletFloorMap ---- */}
          <div className="relative flex-1 min-h-[500px] bg-slate-200">
            {bgImage ? (
              <LeafletFloorMap
                imageUrl={bgImage}
                imageWidth={floorData?.imageWidth}
                imageHeight={floorData?.imageHeight}
                shapes={shapes}
                markers={markers}
                onMapClick={onMapClick}
                className="w-full h-full"
              />
            ) : (
              <div className="absolute inset-0 flex items-center justify-center text-slate-400 text-sm">
                {floorIdParam ? t("admin.additionalLocation.floorMapEmpty", "ไม่มีรูปแผนผังของชั้นนี้") : t("admin.additionalLocation.selectFloorPrompt", "กรุณาเลือกชั้น")}
              </div>
            )}

            {/* Coordinate display in bottom-right */}
            <div className="absolute bottom-4 right-4 bg-slate-900/85 text-white font-mono text-xs px-3 py-1.5 rounded shadow-lg backdrop-blur-sm z-50">
              {activePoint ? (
                (() => {
                  const p = points.find((x) => x.id === activePoint);
                  return p ? `X: ${Math.round(p.x)}, Y: ${Math.round(p.y)}` : "X: 0, Y: 0";
                })()
              ) : form.x && form.y ? (
                `X: ${Math.round(Number(form.x))}, Y: ${Math.round(Number(form.y))}`
              ) : (
                "X: 0, Y: 0"
              )}
            </div>
          </div>
        </div>

        {/* ===== RIGHT: ฟอร์มแก้ไข 30% ===== */}
        <div className="w-[30%] min-w-[320px] h-full flex flex-col border-l border-gray-200 bg-white">

          <div className="flex-1 min-h-0 overflow-y-auto p-4">
            <div className="font-semibold text-[17px] text-slate-900 border-b border-slate-200 pb-4">
              {t("admin.additionalLocation.locationDetails", "รายละเอียดสถานที่เพิ่มเติม")}
              {activePoint && (
                <span className="text-yellow-500 m-2">{t("admin.additionalLocation.editActiveId", { defaultValue: "(แก้ไข ID: {{id}})", id: activePoint })}</span>
              )}
            </div>

            {/* อัปโหลดรูปภาพ */}
            <div className="px-1 py-2" />
            <p className="text-sm text-slate-900 m-1 font-semibold">
              {t("admin.additionalLocation.imageFileLabel", "ไฟล์รูปภาพ")} <span className="text-red-500">*</span>
            </p>
            <label
              className="flex flex-col items-center justify-center rounded-md cursor-pointer px-7 py-7 m-2 bg-cover bg-center min-h-[150px] transition hover:opacity-95"
              style={{
                backgroundImage: `linear-gradient(rgba(0,0,0,0.5), rgba(0,0,0,0.5)), url('${getUploadPreviewUrl()}')`,
              }}
            >
              <UploadIcon className="text-white w-6 h-6" />
              <p className="text-sm mt-2 font-medium text-blue-300">
                {t("admin.additionalLocation.addImageUpload", "เพิ่มรูปภาพที่ต้องการอัปโหลด")}
              </p>
              <p className="text-xs text-slate-300">JPEG, PNG</p>
              <input
                type="file"
                accept=".jpg,.jpeg,.png"
                className="hidden"
                onChange={(e) => setImageFile(e.target.files?.[0] || null)}
              />
              {imageFile && (
                <p className="text-xs text-emerald-400 mt-2 bg-slate-900/60 px-2 py-0.5 rounded truncate max-w-full">
                  {imageFile.name}
                </p>
              )}
            </label>

            {/* Category selection */}
            <div className="px-1 py-2">
              <label className="text-sm mt-3 block mb-1 font-semibold text-slate-700">
                {t("admin.additionalLocation.locationTypeLabel", "ประเภทสถานที่")} <span className="text-red-500">*</span>
              </label>
              <select
                value={form.category}
                onChange={(e) => {
                  const newCat = e.target.value;
                  setForm({ ...form, category: newCat });
                  const poiCategories = ["stairs", "elevator", "bathroom", "counterservice"];
                  if (!activePoint) {
                    setActiveTool(newCat);
                  } else if (poiCategories.includes(activeTool)) {
                    setActiveTool(newCat);
                  }
                }}
                className="w-full border border-slate-300 rounded-md p-3 text-sm h-11 bg-white focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="bathroom">{t("admin.additionalLocation.bathroom", "ห้องน้ำ (Bathroom)")}</option>
                <option value="stairs">{t("admin.additionalLocation.stairs", "บันได (Stairs)")}</option>
                <option value="elevator">{t("admin.additionalLocation.elevator", "ลิฟต์ (Elevator)")}</option>
                <option value="counterservice">{t("admin.additionalLocation.counterService", "จุดบริการ/ประชาสัมพันธ์ (Counter Service)")}</option>
              </select>
            </div>

            {/* ชื่อสถานที่ */}
            <div className="px-1 py-2">
              <label className="text-sm mt-3 block mb-1 font-semibold text-slate-700">
                {t("admin.additionalLocation.locationNameLabel", "ชื่อสถานที่")} <span className="text-red-500">*</span>
              </label>

              {names.map((name, index) => (
                <div key={index} className="flex items-center gap-2 mb-2">
                  <input
                    type="text"
                    placeholder={
                      index === 0
                        ? t("admin.additionalLocation.locationNamePlaceholder", "เช่น ห้องน้ำชาย, บันได A")
                        : `${t("admin.additionalLocation.locationAliasPlaceholder", "ชื่ออื่น ๆ")} ${index}`
                    }
                    value={name}
                    onChange={(e) => {
                      const newNames = [...names];
                      newNames[index] = e.target.value;
                      setNames(newNames);
                    }}
                    className="w-full border border-slate-300 rounded-md p-3 text-sm h-10 focus:outline-none focus:ring-2 focus:ring-blue-500 transition"
                  />
                  {index > 0 && (
                    <button
                      type="button"
                      onClick={() => {
                        const newNames = names.filter((_, i) => i !== index);
                        setNames(newNames);
                      }}
                      className="px-3 h-10 text-red-500 hover:bg-red-50 rounded-md border border-red-200 text-xs font-semibold shrink-0 transition"
                    >
                      {t("common.delete", "ลบ")}
                    </button>
                  )}
                </div>
              ))}

              <button
                type="button"
                onClick={() => setNames([...names, ""])}
                className="mt-1 px-4 py-1.5 border border-dashed border-blue-300 text-blue-600 hover:bg-blue-50 rounded-lg text-xs font-bold transition flex items-center gap-1 mb-3"
              >
                <span>+</span> {t("admin.additionalLocation.addAliasBtn", "เพิ่มชื่ออื่น")}
              </button>
            </div>

            {/* พิกัดบนแผนที่ */}
            <label className="text-sm flex px-1 mt-4 mb-1 font-semibold text-slate-700 items-center">
              <LocationIcon className="text-blue-700 w-4 h-4 mr-2" />
              {t("admin.additionalLocation.mapCoordinatesLabel", "พิกัดบนแผนที่ (%)")}
            </label>
            <div className="flex px-1 py-0 mb-4 gap-2">
              <input
                type="text"
                placeholder="x-coord"
                value={form.x}
                onChange={(e) => setForm({ ...form, x: e.target.value })}
                className="w-full border border-slate-300 rounded-md p-3 text-sm h-10
                  focus:outline-none focus:ring-2 focus:ring-blue-500 transition"
              />
              <input
                type="text"
                placeholder="y-coord"
                value={form.y}
                onChange={(e) => setForm({ ...form, y: e.target.value })}
                className="w-full border border-slate-300 rounded-md p-3 text-sm h-10
                  focus:outline-none focus:ring-2 focus:ring-blue-500 transition"
              />
            </div>

            <div className="px-2 mb-10">
              <button
                onClick={() => setShowConfirmDelete(true)}
                disabled={!activePoint}
                className="w-full text-sm border border-red-100 bg-red-50 text-red-700 rounded-lg p-2 font-semibold disabled:opacity-50 hover:bg-red-100 transition"
              >
                {t("admin.additionalLocation.deleteLocation", "ลบสถานที่")}
              </button>
              {errMsg && (
                <div className="mt-2 text-sm text-red-600 font-semibold">{errMsg}</div>
              )}
            </div>
          </div>

          {/* ---- Footer ปุ่ม ---- */}
          <div className="shrink-0 border-t border-slate-100 p-3 bg-slate-50">
            <div className="flex justify-center gap-2">
              <button
                onClick={() => {
                  setActivePoint(null);
                  setForm({ name: "", category: "bathroom", x: "", y: "" });
                  setImageFile(null);
                }}
                className="flex-1 py-2 bg-white text-slate-700 rounded-lg border border-gray-200 font-medium hover:bg-gray-50 transition"
              >
                {t("admin.additionalLocation.cancel", "ยกเลิก")}
              </button>
              <button
                onClick={onSave}
                disabled={saving}
                className="flex-1 py-2 bg-yellow-500 text-white rounded-lg font-medium hover:bg-yellow-600 transition disabled:opacity-60"
              >
                {saving ? t("admin.additionalLocation.saving", "กำลังบันทึก...") : activePoint ? t("admin.additionalLocation.edit", "แก้ไข") : t("admin.additionalLocation.add", "เพิ่ม")}
              </button>
            </div>
          </div>
        </div>
      </div>

      {showConfirmDelete && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[9999] p-4">
          <div className="bg-white rounded-xl shadow-2xl p-6 max-w-sm w-full border border-slate-100">
            <h3 className="text-lg font-bold text-slate-900 mb-2">{t("admin.additionalLocation.confirmDeleteTitle", "ยืนยันการลบ")}</h3>
            <p className="text-sm text-slate-600 mb-6">{t("admin.additionalLocation.confirmDeleteDesc", "คุณแน่ใจหรือไม่ว่าต้องการลบสถานที่เพิ่มเติมนี้?")}</p>
            <div className="flex gap-3 justify-end">
              <button
                onClick={() => setShowConfirmDelete(false)}
                className="px-4 py-2 border border-slate-200 rounded-lg text-sm text-slate-700 font-semibold hover:bg-slate-50 transition"
              >
                {t("admin.additionalLocation.cancel", "ยกเลิก")}
              </button>
              <button
                onClick={handleConfirmDelete}
                className="px-4 py-2 bg-red-600 text-white rounded-lg text-sm font-semibold hover:bg-red-700 transition"
              >
                {t("admin.additionalLocation.confirmDeleteTitle", "ยืนยันการลบ")}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
